import { sortBy, uniqBy } from 'lodash';
import { useMemo } from 'react';
import { itemHasContent } from '../../../data/filters/directory/itemHasContent';
import { useLocale } from '../../../data/hooks/i18nHooks';
import { useEventNews } from '../../../data/hooks/news/useEventNews';
import { useEventNotifications } from '../../../data/hooks/notifications/useEventNotifications';
import { getTargetURL } from '../../../misc/getTargetURL';
import NewsIcon from './icons/news.png';
import NotificationIcon from './icons/notification.png';
import { isChatNotification } from '../../../data/filters/notifications/isChatNotification';
import { isMeetingNotification } from '../../../data/filters/notifications/isMeetingNotification';
import { isQANotification } from '../../../data/filters/notifications/isQANotification';

export function useMergedNotifications(eventId, { filter } = {}) {
    const news = useEventNews(eventId),
        notifications = useEventNotifications(eventId, {
            filter: (notif) =>
                !isChatNotification(notif) &&
                !isMeetingNotification(notif) &&
                !isQANotification(notif),
        }),
        locale = useLocale(),
        result = useMemo(() => {
            let result = [];

            // Append news
            result = result.concat(
                news.map((item) => ({
                    uid: `news-${item.id}`,
                    title: item.title,
                    timestamp: item.date_published,
                    href: itemHasContent(item)
                        ? ['.', 'news', item.id].join('/')
                        : item.target
                        ? getTargetURL(eventId, item.target)
                        : null,
                    icon: NewsIcon,
                    read: notifications.find(
                        (notif) => notif.content.data.newsId == item.id
                    )?.read,
                }))
            );

            // Append notifications
            result = result.concat(
                notifications.map((item) => ({
                    uid: genNotificationUID(item),
                    title:
                        item.content.message[locale] ?? item.content.message.en,
                    timestamp: item.sent_on,
                    href: ['.', item.id].join('/'),
                    icon: NotificationIcon,
                    read: item.read,
                }))
            );
            // Remove duplicated ones
            result = uniqBy(result, 'uid');

            // Apply filter
            if (filter) result = result.filter(filter);

            // Sort
            result = sortBy(result, 'timestamp');
            result = result.reverse();

            return result;
        }, [news, notifications, locale]);

    return result;
}

function genNotificationUID(item) {
    if (item.content.data.newsId) {
        return `news-${item.content.data.newsId}`;
    }

    return `notification-${item.id}`;
}

export function unreadFilter(notif) {
    return !notif.read;
}
